import React from 'react';
import { Line } from 'react-chartjs-2';
import 'chart.js/auto';
import { ChartOptions } from 'chart.js';
import TooltipPopover from 'components/TooltipPopover';
import DownloadMenu from 'components/DownloadMenu';
import { noDataPlugin } from 'utils';
interface SearchTermsProps {
  data: {
    data: {
      period: string;
      total_demand: number;
    }[];
  } | null;
  uniqueQueryCount: {
    total_unique_query_count: number;
  } | null;
  yoyGrowthDemand: {
    total_demand_previous_year: number;
    total_demand_current_year: number;
    demand_growth_ratio: number;
  } | null;
  grandTotalDemand: number | null;
}

const formatNumber = (num: number): string => {
  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(1) + 'B';
  } else if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  } else {
    return num.toString();
  }
};

const SearchTerms: React.FC<SearchTermsProps> = ({
  data,
  uniqueQueryCount,
  yoyGrowthDemand,
  grandTotalDemand,
}) => {
  const demandData = data ? data.data : [];
  const labels = demandData.map((item) => item.period);
  const chartData = demandData.map((item) => item.total_demand);

  const revenueOverviewData = {
    labels: labels,
    datasets: [
      {
        label: 'Trends',
        data: chartData,
        borderColor: '#222222',
        borderWidth: 1.5,
        fill: false,
        pointBackgroundColor: '#FFFFFF',
        pointBorderColor: '#222222',
        pointRadius: 1.5,
        tension: 0.2,
      },
    ],
  };

  const formatYAxisTick = (tickValue: number) => {
    if (tickValue >= 1_000_000) {
      return `${(tickValue / 1_000_000).toFixed(1)}M`;
    } else if (tickValue >= 1_000) {
      return `${(tickValue / 1_000).toFixed(1)}K`;
    } else {
      return tickValue.toString();
    }
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Month',
          color: '#4D4D4D',
          font: {
            size: 14,
            style: 'normal',
            weight: 700,
            lineHeight: 1.28,
            family: 'DM Sans',
          },
        },
        ticks: {
          color: '#333',
          font: {
            size: 14,
            style: 'normal',
            weight: 500,
            lineHeight: 1.28,
            family: 'DM Sans',
          },
          maxRotation: 0,
          minRotation: 0,
        },
        grid: {
          color: '#B3B3B3',
        },
        border: {
          color: '#333333',
          display: true,
        },
      },
      y: {
        title: {
          display: true,
          text: 'Demand',
          color: '#4D4D4D',
          font: {
            size: 14,
            style: 'normal',
            weight: 700,
            lineHeight: 1.28,
            family: 'DM Sans',
          },
        },
        ticks: {
          display: true,
          color: '#333',
          font: {
            size: 14,
            style: 'normal',
            weight: 500,
            lineHeight: 1.28,
            family: 'DM Sans',
          },
          align: 'center',
          callback: (tickValue: string | number) => {
            if (typeof tickValue === 'number') {
              return formatYAxisTick(tickValue);
            }
            return tickValue;
          },
        },
        grid: {
          color: '#B3B3B3',
        },
        border: {
          color: '#333333',
          display: true,
        },
      },
    },
  };

  return (
    <div
      className="bg-light rounded-2xl shadow-sm mt-8"
      id="searchTrendsOverTime"
    >
      <div className="flex justify-between items-center border-b border-success mb-2">
        <div>
          <h2 className="text-primarygray text-2xl font-bold mb-1 pl-4 pt-2">
            Search Trends Over Time
          </h2>
          <h3 className="text-gray800 text-sm font-bold mb-2 pl-4">
            Evolution of your category
          </h3>
        </div>
        <DownloadMenu
          id="downloadDropdownMenu-searchTrendsOverTime"
          XLSParams={{
            filename: 'searchTrendsOverTime',
            headers: ['date', 'demand'],
            labels: revenueOverviewData.labels,
            columns: revenueOverviewData.datasets.map((item) => item.data),
          }}
          imageParam="searchTrendsOverTime"
        />
      </div>
      <div className="flex relative">
        <div className="flex relative pl-2 py-6 justify-center items-center md:h-[295px] 2xl:w-[85%] xl:w-[85%] md:w-[85%] w-[85%]">
          <Line
            data={revenueOverviewData}
            options={options}
            plugins={[noDataPlugin]}
          />
        </div>
        <div className="flex flex-col justify-center items-center pl-4 pr-4 py-6 2xl:w-[15%] xl:w-[15%] md:w-[15%] w-[15%]">
          <div className="mb-4 text-center">
            <TooltipPopover
              content="consumer_intent_page.overview_tab.demand"
              type="dashed"
              side="top"
            >
              <h4 className="text-thirdgray text-sm">Demand</h4>
            </TooltipPopover>
            <p className="text-[32px] text-primarygray">
              {grandTotalDemand ? formatNumber(grandTotalDemand) : 'N/A'}
            </p>
          </div>
          <div className="mb-4 text-center">
            <TooltipPopover
              content="consumer_intent_page.overview_tab.unique_query_count"
              type="dashed"
              side="top"
            >
              <h4 className="text-thirdgray text-sm">Unique Query Count</h4>
            </TooltipPopover>
            <p className="text-[32px] text-primarygray">
              {uniqueQueryCount
                ? formatNumber(uniqueQueryCount.total_unique_query_count)
                : 'N/A'}
            </p>
          </div>
          <div className="mb-4 text-center">
            <TooltipPopover
              content="consumer_intent_page.overview_tab.yoy_growth"
              type="dashed"
              side="top"
            >
              <h4 className="text-thirdgray text-sm">YoY Growth</h4>
            </TooltipPopover>
            <p className="text-[32px] text-primarygray">
              {yoyGrowthDemand
                ? (yoyGrowthDemand.demand_growth_ratio * 100).toFixed(2) + '%'
                : 'N/A'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchTerms;
